/**
 * Admin Session Manager
 * Управление сессиями администраторов (полностью отдельно от пользовательских)
 *
 * Follows DRY, SOLID principles
 */

import { ref, computed, watch } from 'vue'
import { consola } from 'consola'

const logger = consola.withTag('admin-session-manager')

// Константы для админских токенов
const ADMIN_ACCESS_TOKEN_KEY = 'adminAccessToken'
const ADMIN_REFRESH_TOKEN_KEY = 'adminRefreshToken'
const ADMIN_TOKEN_EXPIRY_KEY = 'adminTokenExpiry'
const ADMIN_ACCESS_TOKEN_MAX_AGE_SECONDS = 60 * 60 // 1 час
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000 // 5 минут до истечения

// Время истечения токена (в памяти)
const adminTokenExpiryTime = ref<number | null>(null)

// ✅ SINGLETON: Глобальные ref для админских токенов
const adminAccessToken = ref<string | null>(null)
const adminRefreshToken = ref<string | null>(null)

// Флаг инициализации
let isInitialized = false
let isInitializing = false
let initPromise: Promise<void> | null = null

// Функция инициализации
function initializeSession(): Promise<void> | undefined {
  /* v8 ignore next */
  if (isInitialized) return
  /* v8 ignore next */
  if (isInitializing) return initPromise

  isInitializing = true
  initPromise = new Promise((resolve) => {
    /* v8 ignore start */
    if (!process.client) {
      resolve()
      return
    }
    /* v8 ignore stop */

    logger.info('🔍 Инициализация useAdminSessionManager - проверка localStorage...')

    // Восстанавливаем adminAccessToken
    const storedAccessToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY)
    if (storedAccessToken) {
      adminAccessToken.value = storedAccessToken
      logger.info('✅ Admin AccessToken восстановлен из localStorage')
    }

    // Восстанавливаем adminRefreshToken
    const storedRefreshToken = localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY)
    if (storedRefreshToken) {
      adminRefreshToken.value = storedRefreshToken
      logger.info('✅ Admin RefreshToken восстановлен из localStorage')
    }

    // Восстанавливаем время истечения
    const storedExpiry = localStorage.getItem(ADMIN_TOKEN_EXPIRY_KEY)
    if (storedExpiry) {
      adminTokenExpiryTime.value = Number.parseInt(storedExpiry)

      const now = Date.now()
      const timeRemaining = adminTokenExpiryTime.value - now
      const isExpired = timeRemaining <= 0

      logger.info('🕐 Время истечения админского токена:', {
        expiryTime: new Date(adminTokenExpiryTime.value).toISOString(),
        timeRemainingSeconds: Math.floor(timeRemaining / 1000),
        isExpired,
      })

      if (isExpired) {
        logger.warn('⚠️ Админский токен истек')
      }
    }

    isInitialized = true
    isInitializing = false
    resolve()
  })

  return initPromise
}

// Инициализация при загрузке модуля
if (process.client) {
  initializeSession()

  // Синхронизация с localStorage
  watch(adminAccessToken, (newValue) => {
    if (newValue) {
      localStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, newValue)
      logger.debug('🔄 Admin AccessToken обновлен')
    } else {
      localStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY)
      logger.debug('🗑️ Admin AccessToken удален')
    }
  })

  watch(adminRefreshToken, (newValue) => {
    if (newValue) {
      localStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, newValue)
      logger.debug('🔄 Admin RefreshToken обновлен')
    } else {
      localStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY)
      logger.debug('🗑️ Admin RefreshToken удален')
    }
  })
}

export const useAdminSessionManager = () => {
  // Убеждаемся что инициализация завершена
  /* v8 ignore next 3 */
  if (process.client && !isInitialized && !isInitializing) {
    initializeSession()
  }

  /**
   * Ждать инициализации токенов
   */
  const waitForInit = async () => {
    /* v8 ignore next */
    if (isInitialized) return
    /* v8 ignore next 2 */
    if (initPromise) await initPromise
  }

  /**
   * Проверка наличия активной админской сессии
   */
  const hasAdminSession = computed(() => {
    const hasTokens = !!(adminAccessToken.value || adminRefreshToken.value)

    if (process.dev) {
      logger.debug('🔍 [hasAdminSession] Проверка:', {
        hasTokens,
        accessToken: adminAccessToken.value ? `${adminAccessToken.value.slice(0, 20)}...` : null,
        refreshToken: adminRefreshToken.value ? `${adminRefreshToken.value.slice(0, 20)}...` : null,
      })
    }

    return hasTokens
  })

  /**
   * Проверить, истек ли админский access token
   */
  const isAdminTokenExpired = (): boolean => {
    if (!adminTokenExpiryTime.value) {
      return false
    }

    const now = Date.now()
    const isExpired = now >= adminTokenExpiryTime.value

    if (isExpired) {
      const timeRemaining = adminTokenExpiryTime.value - now
      logger.error('❌ Admin access token истек!', {
        expiredAgo: `${Math.abs(timeRemaining / 1000).toFixed(0)} секунд назад`,
      })
    }

    return isExpired
  }

  /**
   * Проверить, нужно ли обновить админский токен
   */
  const shouldRefreshAdminToken = (): boolean => {
    if (!adminTokenExpiryTime.value) {
      return false
    }

    const now = Date.now()
    const timeRemaining = adminTokenExpiryTime.value - now
    return timeRemaining <= TOKEN_REFRESH_THRESHOLD_MS
  }

  /**
   * Установка админской сессии
   */
  const setAdminSession = (accessToken: string, refreshToken: string): void => {
    logger.info('🔧 setAdminSession вызван')

    // Устанавливаем токены
    adminAccessToken.value = accessToken
    adminRefreshToken.value = refreshToken

    // Вычисляем время истечения
    const now = Date.now()
    const expiryTime = now + (ADMIN_ACCESS_TOKEN_MAX_AGE_SECONDS * 1000)
    adminTokenExpiryTime.value = expiryTime

    // Сохраняем в localStorage синхронно (watch асинхронен — nextTick)
    if (process.client) {
      localStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, accessToken)
      localStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, refreshToken)
      localStorage.setItem(ADMIN_TOKEN_EXPIRY_KEY, expiryTime.toString())
    }

    logger.success('✅ Админская сессия установлена', {
      expiresIn: `${ADMIN_ACCESS_TOKEN_MAX_AGE_SECONDS} секунд`,
      expiryTime: new Date(expiryTime).toISOString(),
    })
  }

  /**
   * Обновление админских токенов
   */
  const updateAdminTokens = (accessToken: string, refreshToken: string): void => {
    logger.info('🔄 Обновление админских токенов')
    setAdminSession(accessToken, refreshToken)
  }

  /**
   * Очистка админской сессии
   */
  const clearAdminSession = (): void => {
    logger.info('🗑️ Очистка админской сессии')

    adminAccessToken.value = null
    adminRefreshToken.value = null
    adminTokenExpiryTime.value = null

    if (process.client) {
      localStorage.removeItem(ADMIN_TOKEN_EXPIRY_KEY)
    }

    logger.success('✅ Админская сессия очищена')
  }

  /**
   * Получить админский access token
   */
  const getAdminAccessToken = (): string | null => {
    return adminAccessToken.value
  }

  /**
   * Получить админский refresh token
   */
  const getAdminRefreshToken = (): string | null => {
    return adminRefreshToken.value
  }

  /**
   * Извлечь роль администратора из access-токена (payload.role).
   * Источник правды для гейтинга доступа по ролям (см. shared/config/adminAccess).
   * Возвращает null, если токена нет или он не парсится.
   */
  const getAdminRole = (): string | null => {
    const token = adminAccessToken.value
    if (!token) return null

    try {
      const payloadPart = token.split('.')[1]
      if (!payloadPart) return null

      // base64url → base64
      const base64 = payloadPart.replaceAll('-', '+').replaceAll('_', '/')
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
      const json = atob(padded)
      const payload = JSON.parse(json) as { role?: unknown }

      return typeof payload.role === 'string' ? payload.role : null
    } catch (error) {
      logger.warn('Не удалось извлечь роль из админского токена:', error)
      return null
    }
  }

  /**
   * ID текущего администратора из админского JWT (payload.sub).
   * Возвращает null, если токена нет или он не парсится.
   */
  const getAdminId = (): string | null => {
    const token = adminAccessToken.value
    if (!token) return null

    try {
      const payloadPart = token.split('.')[1]
      if (!payloadPart) return null

      const base64 = payloadPart.replaceAll('-', '+').replaceAll('_', '/')
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
      const json = atob(padded)
      const payload = JSON.parse(json) as { sub?: unknown }

      return typeof payload.sub === 'string' ? payload.sub : null
    } catch (error) {
      logger.warn('Не удалось извлечь id из админского токена:', error)
      return null
    }
  }

  return {
    // State (readonly для внешнего использования)
    adminAccessToken: computed(() => adminAccessToken.value),
    adminRefreshToken: computed(() => adminRefreshToken.value),

    // Computed
    hasAdminSession,

    // Methods
    waitForInit,
    setAdminSession,
    updateAdminTokens,
    clearAdminSession,
    isAdminTokenExpired,
    shouldRefreshAdminToken,
    getAdminAccessToken,
    getAdminRefreshToken,
    getAdminRole,
    getAdminId,
  }
}


